import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";

import "react-toastify/dist/ReactToastify.css";

const AdministratorsForm = ({ settings }) => {
  const s = useSkeleton("abm");
  const { t } = useTranslation();

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";

  const {
    unregister,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      nick: "",
      password: "",
      notes: "",
      active: 0,
    },
  });

  const [isLoading, setIsLoading] = useAtom(loadingState);
  const [isVisible, setIsVisible] = useState(!(isEditMode || isDeleteMode));

  const formModeConfig = {};

  if (isEditMode) {
    formModeConfig.SubmitButtonText = t("buttons.update");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.updateEntity(data, "base");
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(settings.entityId, "base");
    };
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.createEntity(data, "base");
    };
  }

  useEffect(() => {
    if (isEditMode || isDeleteMode) {
      const fetchData = async () => {
        try {
          const data = await settings.CRUDService.getEntity(
            settings.entityId,
            "base"
          );

          for (const key in data) {
            setValue(key, data[key]);
          }
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      };

      fetchData();
    }
  }, [settings.entityId, setValue, settings.formMode]);

  const handleShow = (e) => {
    e.preventDefault();
    if (isVisible) {
      unregister("password");
    }
    setIsVisible(!isVisible);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);

      settings.hideDialog();

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      settings.toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <div className={s.get()}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`${s.get("form")} ${settings.formMode !== "DELETE" ? "abm-create-form" : ""}`}
        >
          {!isDeleteMode && (
            <div className="abm-form-section-title">
              <i className="pi pi-user"></i>
              <span>Detalles de identidad</span>
            </div>
          )}

          <Controller
            name="name"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.administrators.name"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.administrators.name"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="name">
                    {t("entities.administrators.name")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.name)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="name"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.administrators.phone"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="phone">
                    {t("entities.administrators.phone")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.phone)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="phone"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={
              settings.formMode == "DELETE"
                ? ""
                : {
                  pattern: {
                    value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                    message: t("forms.validation.noValidEmail"),
                  },
                  maxLength: {
                    value: 60,
                    message: t("forms.validation.maxLength", {
                      entity: t("entities.administrators.email"),
                      maxLength: 60,
                    }),
                  },
                }
            }
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="email">
                    {t("entities.administrators.email")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.email)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="email"
                      {...field}
                      value={field.value || ""}
                      maxLength={60}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      autoComplete="off"
                      disabled={settings.formMode === "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <Controller
            name="nick"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.administrators.nick"),
              }),
              maxLength: {
                value: 500,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.administrators.nick"),
                  maxLength: 500,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="nick">
                    {t("entities.administrators.nick")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.nick)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="nick"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={
              isVisible
                ? {
                  required: t("forms.validation.required", {
                    entity: t("entities.administrators.password"),
                  }),
                  maxLength: {
                    value: 500,
                    message: t("forms.validation.maxLength", {
                      entity: t("entities.administrators.password"),
                      maxLength: 500,
                    }),
                  },
                }
                : ""
            }
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="password">
                    {t("entities.administrators.password")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.password)}
                  </div>
                  <div className="flex ">
                    {isVisible && (
                      <Password
                        id="password"
                        {...field}
                        inputRef={field.ref}
                        className={[
                          "form-control",
                          classNames({
                            "p-invalid": fieldState.invalid,
                          }),
                        ]}
                        maxLength={100}
                        feedback={false}
                        disabled={settings.formMode === "DELETE"}
                        autoComplete="new-password"
                      />
                    )}
                    {isEditMode && (
                      <Button
                        style={{ boxShadow: "none" }}
                        label={
                          isVisible
                            ? t("entities.administrators.hidePassword")
                            : t("entities.administrators.showPassword")
                        }
                        onClick={handleShow}
                        link
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="notes"
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.administrators.notes"),
                  maxLength: 500,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="notes">
                    {t("entities.administrators.notes")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.notes)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="notes"
                      {...field}
                      value={field.value ?? ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="active"
            control={control}
            rules={{}}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="active">
                    {t("entities.administrators.active")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.active)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <Checkbox
                      inputId="active"
                      onChange={(e) => field.onChange(e.checked)}
                      checked={field.value}
                      disabled={settings.formMode == "DELETE"}
                      className="flex mt-2"
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <div className="flex justify-content-between mt-6 mb-2">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <Button
              label={
                isLoading
                  ? formModeConfig.SubmitButtonTextWait
                  : formModeConfig.SubmitButtonText
              }
              icon="pi pi-check"
              className=" btn btn-primary btn-primary-crud"
              type="submit"
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default AdministratorsForm;
